import React from "react";
import { Box, CircularProgress, Link, TablePagination, Typography } from "@mui/material";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import BondTable from "../BondTable";
import { tokens } from "../../Common/designTokens";
import { MA_WATCHLIST_PROCESSING_MESSAGE } from "../../Constants/AppConstants";

const MAInventoryTable = ({
  data,
  selectedColumns,
  sortConfig,
  onSort,
  page,
  itemsPerPage,
  totalElements,
  onPageChange,
  onRowsPerPageChange,
  maInventoryData,
  loading = false,
  hasWatchlists = true,
  isProcessing = false,
  onCreateWatchlistClick,
  onEditWatchlistClick,
  isDailyProcessBlocked = false,
  dailyProcessStatus = {},
  hasActiveFilters = false,
  onClearFilters,
  flashRowCusips,
  activeFilterColumnKeys,
}) => {
  const hasRows = Array.isArray(data) && data.length > 0;
  // Rows exist for this watchlist but the active filter excluded them all —
  // distinguish this from a genuinely empty/absent watchlist.
  const noMatchingRows =
    hasActiveFilters
    && Array.isArray(maInventoryData)
    && maInventoryData.length > 0;
  const dailyProcessMessage =
    dailyProcessStatus?.message
    || "As the daily update is in progress, watchlists are temporarily unavailable for viewing or editing. Please try again later.";

  return (
    <>
      <Box
        sx={{
          padding: "0px",
          height: "calc(100vh - 225px)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {hasRows ? (
          <BondTable
            source="ma-inventory"
            selectedColumns={selectedColumns}
            filteredData={data}
            sectionKey="ma-inventory"
            onSort={onSort}
            sortConfig={sortConfig}
            maInventoryData={maInventoryData}
            scrollResetSignal={`${page}-${itemsPerPage}`}
            flashRowCusips={flashRowCusips}
            filteredColumnKeys={activeFilterColumnKeys}
          />
        ) : isDailyProcessBlocked ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 3,
            }}
          >
            {!loading && (
              <Box
                sx={{
                  maxWidth: 560,
                  borderRadius: "18px",
                  border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                  backgroundColor: tokens.colors.defaultGridRowColor,
                  px: { xs: 3, sm: 4 },
                  py: { xs: 3, sm: 3.5 },
                  textAlign: "center",
                  boxShadow: "0 18px 40px rgba(15, 23, 42, 0.08)",
                }}
              >
                <AutorenewOutlinedIcon
                  sx={{
                    fontSize: 40,
                    color: tokens.colors.amberColor,
                    mb: 1.5,
                  }}
                />
                <Typography
                  sx={{
                    color: tokens.colors.fontColour,
                    fontSize: "1.05rem",
                    fontWeight: 700,
                  }}
                >
                  Daily watchlist update in progress...
                </Typography>
                <Typography
                  sx={{
                    mt: 1,
                    color: tokens.colors.fontColour85,
                    fontSize: "0.95rem",
                    lineHeight: 1.6,
                  }}
                >
                  {dailyProcessMessage}
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 3,
            }}
          >
            {!loading && (
              <Typography
                sx={{
                  color: tokens.colors.fontColour85,
                  fontSize: "18px",
                  textAlign: "center",
                }}
              >
                {isProcessing ? (
                  MA_WATCHLIST_PROCESSING_MESSAGE
                ) : noMatchingRows ? (
                  <>
                    No quotes available for the selected filter criteria. Update
                    the Quotes filters or{" "}
                    <Link
                      component="button"
                      underline="always"
                      onClick={onClearFilters}
                      sx={{
                        color: tokens.palette.primary,
                        fontWeight: 600,
                        cursor: "pointer",
                        verticalAlign: "baseline",
                      }}
                    >
                      Clear Filters
                    </Link>{" "}
                    to view matching results.
                  </>
                ) : hasWatchlists ? (
                  <>
                    Your watchlist is empty. Please{" "}
                    <Link
                      component="button"
                      underline="always"
                      onClick={onEditWatchlistClick}
                      sx={{
                        color: tokens.palette.primary,
                        fontWeight: 600,
                        cursor: "pointer",
                        verticalAlign: "baseline",
                      }}
                    >
                      add
                    </Link>{" "}
                    securities to view market data.
                  </>
                ) : (
                  <>
                    No watchlists found. Please{" "}
                    <Link
                      component="button"
                      underline="always"
                      onClick={onCreateWatchlistClick}
                      sx={{
                        color: tokens.palette.primary,
                        fontWeight: 600,
                        cursor: "pointer",
                        verticalAlign: "baseline",
                      }}
                    >
                      create
                    </Link>{" "}
                    a watchlist to view market data.
                  </>
                )}
              </Typography>
            )}
          </Box>
        )}

        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0, 0, 0, 0.24)",
            }}
          >
            <CircularProgress sx={{ color: tokens.palette.primary }} size={44} />
          </Box>
        )}
      </Box>
      {hasRows && !isDailyProcessBlocked && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100%",
            color: "white",
            zIndex: 1000,
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
          }}
        >
          <TablePagination
            component="footer"
            sx={{
              ".MuiSelect-select": { p: "4px 24px 5px 8px !important" },
            }}
            showFirstButton
            showLastButton
            rowsPerPage={itemsPerPage}
            rowsPerPageOptions={[20, 50, 100]}
            page={page}
            onRowsPerPageChange={onRowsPerPageChange}
            onPageChange={onPageChange}
            count={totalElements}
            labelDisplayedRows={({ from, to, count }) => `${from.toLocaleString()}–${to.toLocaleString()} of ${count.toLocaleString()}`}
          />
        </Box>
      )}
    </>
  );
};

export default MAInventoryTable;
